export type LauncherOffsets = {
    bottom: number;
    right: number;
};

type LauncherOptions = {
    label: string;
    bottom?: number;
    right?: number;
};

export type LauncherController = {
    setLabel: (label: string) => void;
    showButton: () => void;
    hideButton: () => void;
    setOnClick: (handler: () => void) => void;
    getOffsets: () => LauncherOffsets;
    destroy: () => void;
};

const defaultOffsets: LauncherOffsets = {
    bottom: 44,
    right: 44
};

export const defaultLauncherOffsets = defaultOffsets;

const launcherStylesId = 'skf-pa-launcher-styles';
const launcherFontRegularUrl = new URL('../resources/TTF/SKFSansOffice-Regular.ttf', import.meta.url).toString();

export function createLauncherHelper(options: LauncherOptions): LauncherController | undefined {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return undefined;
    }

    return new LauncherControllerImpl({
        label: options.label,
        bottom: options.bottom ?? defaultOffsets.bottom,
        right: options.right ?? defaultOffsets.right
    });
}

class LauncherControllerImpl implements LauncherController {
    private readonly root: HTMLDivElement;
    private readonly button: HTMLButtonElement;
    private readonly labelSpan: HTMLSpanElement;
    private readonly offsets: LauncherOffsets;
    private onClick?: () => void;
    private isVisible = true;

    constructor(options: { label: string; bottom: number; right: number }) {
        ensureLauncherStyles();
        this.offsets = {
            bottom: options.bottom,
            right: options.right
        };

        this.root = document.createElement('div');
        this.root.className = 'skf-pa-launcher';
        this.root.style.bottom = `${this.offsets.bottom}px`;
        this.root.style.right = `${this.offsets.right}px`;

        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.className = 'skf-pa-launcher-button';
        this.button.addEventListener('click', this.handleClick);

        this.labelSpan = document.createElement('span');
        this.labelSpan.className = 'skf-pa-launcher-label';
        this.labelSpan.textContent = options.label;

        this.button.appendChild(createSearchIcon());
        this.button.appendChild(this.labelSpan);
        this.root.appendChild(this.button);
        document.body.appendChild(this.root);
    }

    public setLabel(label: string): void {
        this.labelSpan.textContent = label;
    }

    public showButton(): void {
        if (this.isVisible) {
            return;
        }

        this.isVisible = true;
        this.root.classList.remove('is-hidden');
    }

    public hideButton(): void {
        if (!this.isVisible) {
            return;
        }

        this.isVisible = false;
        this.root.classList.add('is-hidden');
    }

    public setOnClick(handler: () => void): void {
        this.onClick = handler;
    }

    public getOffsets(): LauncherOffsets {
        return { ...this.offsets };
    }

    public destroy(): void {
        this.button.removeEventListener('click', this.handleClick);
        this.root.remove();
    }

    private handleClick = (event: MouseEvent): void => {
        event.preventDefault();
        this.onClick?.();
    };
}

function ensureLauncherStyles(): void {
    if (document.getElementById(launcherStylesId)) {
        return;
    }

    const style = document.createElement('style');
    style.id = launcherStylesId;
    style.textContent = `
@font-face {
    font-family: 'SKF Office Sans';
    src: url('${launcherFontRegularUrl}') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
}

.skf-pa-launcher {
    position: fixed;
    z-index: 2147483001;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.skf-pa-launcher.is-hidden {
    opacity: 0;
    pointer-events: none;
    transform: translateY(4px);
}

.skf-pa-launcher-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 9px 16px;
    border-radius: 4px;
    border: 0.5px solid #c3c4ca;
    background: #0000fe;
    color: #ffffff;
    font-size: 1.10rem;
    font-weight: lighter;
    font-family: 'SKF Office Sans', sans-serif;
    cursor: pointer;
    opacity: 0.97;
    transition: transform 0.15s ease, opacity 0.15s ease;
}

.skf-pa-launcher-button:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 3px;
}

.skf-pa-launcher-button:hover {
    transform: translateY(-1px);
}

.skf-pa-launcher-button:active {
    transform: translateY(1px);
    opacity: 0.92;
}

.skf-pa-launcher-button svg {
    width: 24px;
    height: 24px;
}

.skf-pa-launcher-label {
    white-space: nowrap;
}
`;

    document.head.appendChild(style);
}

function createSearchIcon(): SVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M13.3242 8.1123C13.6497 7.34701 14.6871 7.29908 15.1016 7.96875L15.1758 8.1123L15.5977 9.10352C16.3249 10.8135 17.6865 12.1751 19.3965 12.9023L20.3877 13.3242C21.204 13.6714 21.204 14.8286 20.3877 15.1758L19.3965 15.5977C17.6865 16.3249 16.3249 17.6865 15.5977 19.3965L15.1758 20.3877C14.8286 21.204 13.6714 21.204 13.3242 20.3877L12.9023 19.3965C12.1751 17.6865 10.8135 16.3249 9.10352 15.5977L8.1123 15.1758C7.29601 14.8286 7.29601 13.6714 8.1123 13.3242L9.10352 12.9023C10.8135 12.1751 12.1751 10.8135 12.9023 9.10352L13.3242 8.1123Z');
    path1.setAttribute('stroke', 'currentColor');
    path1.setAttribute('stroke-width', '1.5');

    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M5.86269 2.56219C5.91524 2.44271 6.08476 2.44271 6.13731 2.56219L7.02804 4.58739C7.1036 4.75918 7.24082 4.8964 7.41261 4.97196L9.43781 5.86269C9.55729 5.91524 9.55729 6.08476 9.43781 6.13731L7.41261 7.02804C7.24082 7.10359 7.1036 7.24082 7.02804 7.41261L6.13731 9.43781C6.08476 9.55729 5.91524 9.55729 5.86269 9.43781L4.97196 7.41261C4.8964 7.24082 4.75918 7.1036 4.58739 7.02804L2.56219 6.13731C2.44271 6.08476 2.44271 5.91524 2.56219 5.86269L4.58739 4.97196C4.75918 4.8964 4.8964 4.75918 4.97196 4.58739L5.86269 2.56219Z');
    path2.setAttribute('fill', 'currentColor');

    svg.appendChild(path1);
    svg.appendChild(path2);
    return svg;
}
